// Reusable multi-layer paper/halftone texture overlay.
// Renders absolutely-positioned, inset-0 layers — parent must be `relative`.
// Final opacity per layer = base opacity * strength, clamped to 1.

type PaperTextureProps = {
  inkColor?: string;
  strength?: number;
};

const clampOpacity = (base: number, strength: number) =>
  Math.min(1, Math.max(0, base * strength));

const layer = (id: string, inkColor: string, width: number, filter: string) =>
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${width}">${filter.replace(
      /__ID__/g,
      id
    )}<rect width="${width}" height="${width}" fill="${inkColor}" filter="url(#${id})"/></svg>`
  );

export function PaperTexture({ inkColor = "#5C4A33", strength = 1 }: PaperTextureProps) {
  // Layer 1 — broad directional mottle (smooth, stretched on one axis).
  const mottle = layer(
    "ptm",
    inkColor,
    600,
    `<filter id="__ID__"><feTurbulence type="fractalNoise" baseFrequency="0.008 0.02" numOctaves="2"/></filter>`
  );

  // Layer 3 — main halftone dot screen (denser 3-step discrete).
  const halftone = layer(
    "pth",
    inkColor,
    170,
    `<filter id="__ID__"><feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="2" result="noise"/><feComponentTransfer in="noise" result="dots"><feFuncA type="discrete" tableValues="0 0 1"/></feComponentTransfer><feComposite in="SourceGraphic" in2="dots" operator="in"/></filter>`
  );

  // Layer 4 — fine speck pass.
  const fine = layer(
    "ptf",
    inkColor,
    90,
    `<filter id="__ID__"><feTurbulence type="fractalNoise" baseFrequency="1.4" numOctaves="1" result="noise"/><feComponentTransfer in="noise" result="dots"><feFuncA type="discrete" tableValues="0 0 1"/></feComponentTransfer><feComposite in="SourceGraphic" in2="dots" operator="in"/></filter>`
  );

  const layers: Array<{ url: string; size: number; opacity: number }> = [
    { url: mottle, size: 600, opacity: clampOpacity(0.13, strength) },
    { url: halftone, size: 170, opacity: clampOpacity(0.2, strength) },
    { url: fine, size: 90, opacity: clampOpacity(0.13, strength) },
  ];

  return (
    <>
      {layers.map((l, i) => (
        <div
          key={i}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 mix-blend-multiply"
          style={{
            backgroundImage: `url("${l.url}")`,
            backgroundSize: `${l.size}px ${l.size}px`,
            opacity: l.opacity,
            transform: "translateZ(0)",
          }}
        />
      ))}
    </>
  );
}
